import { useRef } from 'react';
import {
  useDataModel,
  extend,
  params,
  alias,
  bool as BoolType,
  arrayOf as ArrayType,
  string as StringType,
  number as NumberType,
  customType,
  shape as shapeType
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { useSpecialBuy } from '../context/SpecialBuyProvider';
import {
  getItemIds, getFulfillableProducts
} from '../utils/utils';
import { INITIAL_NUM_ITEMS_TO_LOAD, MAX_NUM_ITEMS_TO_LOAD } from '../constants';
import {
  SpecialBuyBannerProductPod
} from '../components/SpecialBuyBannerProductPod/SpecialBuyBannerProductPod';

export const useSearchProducts = (category, isFetchAllItemIds) => {
  const { selectedCategory } = useSpecialBuy();
  const { storeId, storeZip, membershipInformation } = useStore();

  /* these strange lines ensure shouldLoad is a one-way change from false to true */
  const isSelected = Object.is(selectedCategory, category);
  const shouldLoad = useRef(isSelected);
  if (isSelected) shouldLoad.current = true;

  const allItemIds = category.itemIds;
  const firstItemIds = allItemIds.slice(0, INITIAL_NUM_ITEMS_TO_LOAD);
  const remainingItemIds = allItemIds.slice(INITIAL_NUM_ITEMS_TO_LOAD, MAX_NUM_ITEMS_TO_LOAD);

  const defaultVariables = {
    storeId,
    zipCode: storeZip,
    dataSource: 'searchNav',
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership,
    skipKPF: true,
    skipInstallServices: true,
    skipSpecificationGroup: true,
    skipSubscribeAndSave: true,
    additionalSearchParams: {
      deliveryZip: storeZip
    }
  };

  const firstItemsResponse = useDataModel('specialBuyItems', {
    variables: {
      ...defaultVariables,
      itemIds: firstItemIds
    },
    ssr: false,
    skip: !firstItemIds.length
      || !shouldLoad.current
  });

  const remainingItemsResponse = useDataModel('specialBuyItems', {
    variables: {
      ...defaultVariables,
      itemIds: remainingItemIds
    },
    ssr: false,
    skip: !isFetchAllItemIds
      || !remainingItemIds.length
      || !shouldLoad.current
  });

  const products = getFulfillableProducts(
    firstItemsResponse?.data?.searchModel?.products,
    remainingItemsResponse?.data?.searchModel?.products
  );

  return {
    products,
    itemIdsToDisplay: getItemIds(products),
    isLoading: firstItemsResponse?.loading || remainingItemsResponse?.loading,
  };
};

useSearchProducts.dataModel = extend({
  specialBuyItems: alias('searchModel').params({
    itemIds: ArrayType(StringType()),
    isBrandPricingPolicyCompliant: BoolType(),
    additionalSearchParams: customType('AdditionalParams').shape({
      deliveryZip: StringType()
    }),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: StringType(),
      programTiers: ArrayType(
        shapeType({
          tier: StringType(),
          program: StringType()
        })
      )
    })
  }).shape({
    products: params({ startIndex: NumberType() }).arrayOf(
      SpecialBuyBannerProductPod.dataModel.product
    )
  })
});
